// src/services/socket.ts
import { io, Socket } from "socket.io-client";
import { WS_BASE } from "./api"; // should be like "http://localhost:5000"
                                   // DO NOT use ws:// and do NOT append /ws

let socket: Socket | null = null;

// Persistent listener registry. The socket instance is recreated only when it is fully
// torn down (disconnect() nulls it on logout) — socket.io itself reuses one instance and
// preserves listeners across its own reconnections. We keep handlers here so they can be
// bound now (if a socket exists) and re-bound once if a brand-new socket is created later,
// returning an unsubscribe fn.
type SocketHandler = (payload: any) => void;
const eventListeners = new Map<string, Set<SocketHandler>>();

function bindRegisteredListeners(s: Socket) {
  eventListeners.forEach((handlers, event) => {
    handlers.forEach((h) => s.on(event, h));
  });
}

/**
 * Subscribe to a socket event in a reconnect-safe way. Returns an unsubscribe function.
 * Safe to call before the socket connects — the handler binds now (if a socket exists) and
 * is re-bound automatically on every future (re)connection.
 */
export function subscribeToSocket(event: string, handler: SocketHandler): () => void {
  let set = eventListeners.get(event);
  if (!set) { set = new Set(); eventListeners.set(event, set); }
  set.add(handler);
  if (socket) socket.on(event, handler);
  return () => {
    const current = eventListeners.get(event);
    current?.delete(handler);
    if (current && current.size === 0) eventListeners.delete(event);
    if (socket) socket.off(event, handler);
  };
}

export function connectIfAuth(me: any | null) {
  // keep signature flexible; if you have a token put it on me.token
  if (!me) { disconnect(); return; }

  // Reuse the existing instance whenever one exists. Guarding on `socket?.connected` was a
  // bug: during the handshake/reconnect window `socket` is non-null but not yet connected,
  // so a second concurrent call (Layout + Inbox both invoke this) would orphan the in-flight
  // socket and double-bind every registered listener → duplicated realtime events. `socket`
  // is non-null exactly while a live/reconnecting instance exists, and null after disconnect().
  if (socket) return;

  const token = me?.token || localStorage.getItem('dragonsframe_token');

  socket = io(WS_BASE, {
    path: "/socket.io",          // default on server unless you changed it
    withCredentials: true,
    transports: ["websocket"],   // avoid long-polling noise
    // If you have a token available, expose it as me.token and it'll be sent.
    auth: token ? { token } : undefined,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
  });

  socket.on("connect", () => {
    // If your server expects an explicit auth event instead of handshake:
    // if (me?.token) socket!.emit("auth", me.token);   
  });

  socket.on("disconnect", () => { /* optional: toast/log */ });
  socket.on("connect_error", () => { /* optional: toast/log */ });

  // Re-attach any handlers registered via subscribeToSocket() onto this fresh socket.
  bindRegisteredListeners(socket);
}

export function getSocket() { return socket; }

export function disconnect() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

// Keep backward compatibility for existing imports:
export const close = disconnect;
